import {
  ManagementCompanyFragmentFragment,
  useGetManagementCompanyQuery,
  useSearchManagementCompaniesQuery,
  ZyusetuFieldKeyFragmentFragment,
} from "@/graphql/graphql";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {AutocompleteInput} from "@/components/input/Autocomplete.tsx";
import {useEffect, useState} from "react";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {useFormContext, useWatch} from "react-hook-form";
import {createFilterOptions, useTheme} from "@mui/material";
import {Plus} from "lucide-react";
import {CreateNewManagementCompanyDialog} from "@/components/dialog/CreateNewManagementCompanyDialog.tsx";
import {useQueryClient} from "@tanstack/react-query";

const filter = createFilterOptions<Partial<ManagementCompanyFragmentFragment>>();

interface ZyusetuManagementCompanyFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: () => void;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
  ) => Promise<void>;
  isAvailable: boolean;
}

export function ZyusetuManagementCompanyField({
                                                fieldKey,
                                                updateField,
                                                isAvailable,
                                              }: ZyusetuManagementCompanyFieldProps) {
  const [managementCompanyString, setManagementCompanyString] = useState("");
  const [managementCompany, setManagementCompany] =
    useState<Partial<ManagementCompanyFragmentFragment> | null>();
  const search = useSearchManagementCompaniesQuery({
    searchString: managementCompanyString,
  }, {
    enabled: !!managementCompanyString,
  })
  
  const searchKey = useSearchManagementCompaniesQuery.getKey({
    searchString: managementCompanyString,
  })
  const queryClient = useQueryClient()
  
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  
  const {control} = useFormContext();
  
  const watch = useWatch({
    name: fieldKey.keyName,
    control,
  });
  
  const {data} = useGetManagementCompanyQuery(
    {
      id: watch.value,
    },
    {
      enabled: !!watch.value,
    },
  );
  
  useEffect(() => {
    if (data?.getManagementCompany) {
      setManagementCompany(data?.getManagementCompany);
    }
  }, [data?.getManagementCompany]);
  
  const theme = useTheme()
  
  return (
    <>
      {openCreateDialog &&
        <CreateNewManagementCompanyDialog
          open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}
          onCreateSuccess={async (value) => {
            setManagementCompany(value);
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, value.id),
            );
            
            queryClient.setQueryData(searchKey, {
              searchManagementCompanies: [
                ...(search.data?.searchManagementCompanies ?? []),
                value,
              ],
            });
          }}
        />}
      <AutocompleteInput
        onChange={async (v) => {
          if (!v) {
            setManagementCompany(null);
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, ""),
            );
            return;
          }
          if (v.id === "new") {
            setOpenCreateDialog(true);
            return
          }
          setManagementCompany(v);
          await updateField(
            fieldKey.id,
            fieldKey.keyName,
            zyusetuValueIntoZodValue(fieldKey, (v.id) ?? ""),
          );
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          filtered.push({name: "\"新規追加\"", id: "new"});
          return filtered;
        }}
        renderOption={(props, option) => {
          if (option.id === "new") {
            return <li
              {...props}
              style={{
                color: theme.palette.primary.main,
              }}
            >
              <Plus/>
              新規追加
            </li>
          }
          
          return (
            <li {...props}>
              {option.name}
            </li>
          );
        }}
        disabled={!isAvailable}
        loading={search.isLoading}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={managementCompany as Partial<ManagementCompanyFragmentFragment> ?? null}
        multiple={false}
        getOptionLabel={(option) => `${option.name}`}
        fetchOptions={async (words: string) => {
          setManagementCompanyString(words);
        }}
        options={search.data?.searchManagementCompanies || []}
      /></>
  );
}
